import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CalendarDays, User } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";
import { Reveal } from "@/components/ui-bits";

export default function InsightDetail() {
  const { slug } = useParams();
  const { t, pick, lang } = useLanguage();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/insights/${slug}`).then((r) => {
      setArticle(r.data);
      document.title = `${r.data.seo_title || r.data.title_en} | Stratedge UK`;
      if (r.data.seo_description) {
        document.querySelector('meta[name="description"]')?.setAttribute("content", r.data.seo_description);
      }
    }).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound)
    return <div className="mx-auto max-w-7xl px-4 py-32 text-center text-slate-500" data-testid="article-not-found">{t("notFound.title")}</div>;
  if (!article)
    return <div className="mx-auto max-w-7xl px-4 py-32 text-center text-slate-400">{t("common.loading")}</div>;

  const content = pick(article, "content") || article.content_en;

  return (
    <div data-testid="insight-detail-page">
      <section className="relative h-80 overflow-hidden bg-navy-950">
        <img src={article.featured_image} alt={pick(article, "title")} className="h-full w-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/40 to-transparent" />
      </section>
      <article className="mx-auto max-w-3xl px-4 pb-24 sm:px-6">
        <Reveal className="-mt-24 rounded-lg bg-white p-8 shadow-2xl sm:p-12">
          <Link to="/insights" data-testid="article-back-link" className="mb-5 inline-block text-xs font-bold uppercase tracking-wider text-gold-hover hover:text-navy">
            ← {t("insights.viewAll")}
          </Link>
          <span className="mb-3 block text-xs font-bold uppercase tracking-[0.2em] text-gold-hover">{article.category}</span>
          <h1 className="font-display text-3xl font-semibold leading-tight text-navy sm:text-4xl" data-testid="article-title">{pick(article, "title")}</h1>
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 border-b border-slate-100 pb-5 text-xs text-slate-500">
            {article.author && <span className="inline-flex items-center gap-1.5"><User className="h-3.5 w-3.5 text-gold" />{article.author}</span>}
            {article.publish_date && <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5 text-gold" />{article.publish_date}</span>}
            <span>{Math.max(2, Math.ceil(content.split(" ").length / 220))} {t("common.readTime")}</span>
          </div>
          <div className={`article-body mt-8 ${lang === "ar" ? "text-right" : ""}`}>
            {content.split(/\n+/).filter(Boolean).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <div className="mt-10 rounded-lg bg-navy p-7 text-center">
            <h3 className="font-display text-2xl font-semibold text-white">{t("cta.title")}</h3>
            <Link to="/consultation" data-testid="article-consultation-cta" className="mt-5 inline-block rounded-full bg-gold px-7 py-3 text-sm font-bold text-navy hover:bg-gold-hover">
              {t("cta.button")}
            </Link>
          </div>
        </Reveal>
      </article>
    </div>
  );
}
